import { addDoc, doc, updateDoc } from 'firebase/firestore';
import { customFieldsCollectionRef } from '../firebase';
import {
  AuditActor,
  asFirestoreData,
  logActivity,
  stripSentinels,
} from '../audit-log';
import { diffFields } from '../diff-fields';
import type { CustomField } from '../types';

const fieldLabel = (f: { name: string }): string => f.name;

export const createCustomField = async (
  actor: AuditActor | null,
  payload: Record<string, unknown>,
): Promise<string> => {
  // Stamp `is_deleted: false` here (rather than at the call site) so every
  // new field doc satisfies the listener's `where('is_deleted', '==', false)`
  // filter. Firestore omits the field from results when the key is absent,
  // so an unstamped doc would invisibly disappear.
  const fullPayload = { ...payload, is_deleted: false };
  const docRef = await addDoc(customFieldsCollectionRef, fullPayload);
  const snapshot = stripSentinels(fullPayload);
  logActivity({
    actor,
    entity: 'custom-field',
    entityRef: docRef.id,
    entityLabel: fieldLabel(snapshot as { name: string }),
    action: 'create',
    snapshot,
  });
  return docRef.id;
};

export const updateCustomField = async (
  actor: AuditActor | null,
  before: CustomField,
  payload: Record<string, unknown>,
): Promise<void> => {
  await updateDoc(
    doc(customFieldsCollectionRef, before.ref),
    asFirestoreData(payload),
  );
  const changes = diffFields(before, payload);
  if (Object.keys(changes).length === 0) return;
  logActivity({
    actor,
    entity: 'custom-field',
    entityRef: before.ref,
    entityLabel: fieldLabel(before),
    action: 'update',
    changes,
  });
};

// Soft-deletes the field by flipping `is_deleted: true`. The listener
// query filters by `is_deleted == false`, so the field disappears from
// every UI (admin row, attendee form Select, list/roster chips) but
// stored values on attendees stay in place.
//
// We don't hard-wipe attendee values because the attendee list in
// `firebase-context` is active-event-only — a wipe would leave orphan
// `customFields.<ref>` keys on every attendee from any other event.
// Soft-delete sidesteps that and means deletion is also recoverable
// (flip the flag back via Firebase console to restore everything).
export const deleteCustomField = async (
  actor: AuditActor | null,
  before: CustomField,
): Promise<void> => {
  await updateDoc(doc(customFieldsCollectionRef, before.ref), {
    is_deleted: true,
  });
  const { ref, ...rest } = before;
  logActivity({
    actor,
    entity: 'custom-field',
    entityRef: before.ref,
    entityLabel: fieldLabel(before),
    action: 'delete',
    snapshot: rest as unknown as Record<string, unknown>,
    meta: { softDelete: true },
  });
};
